/**
 * Concluir o módulo e repassar ao atendimento — o painel do consultor (item 6 da Amanda).
 *
 * **O botão nunca fica só cinza.** Bloqueado, ele diz por escrito o que falta ("2 de 3 Recursos Plus
 * ainda em aberto"): botão desabilitado sem motivo vira chamado interno. A tela não decide sozinha
 * quando libera — o servidor manda `pode_repassar` e `motivos`, e o `POST` confere de novo.
 *
 * **O repasse é por módulo.** Cada cartão é um módulo, com as três condições, os Recursos Plus
 * dele e o ato. Repassado, o cartão vira o registro: quem, quando, o termo em PDF, o que o Jira
 * fez, o que o e-mail fez — e, quando algo ficou pendente, o motivo escrito e o botão que repete
 * só o que falta.
 *
 * **A reunião não tem módulo no modelo**, então quem declara quais treinamentos pertencem ao
 * módulo é a consultora, na confirmação (o portal só sugere pelo nome). O mesmo para as pendências
 * que seguem no atendimento: são chamados que já existem, com número.
 */

import { useState } from "react";
import { Aviso, Botao, CampoLongo, Cartao, Chip } from "../ui";
import { ErroDaApi } from "../../nucleo/api";
import { mensagemDoConsole } from "../../nucleo/console";
import { formatarInstante } from "../../nucleo/cofre";
import { caminhoDoTermoDeEncerramento } from "../../nucleo/contratoDoCliente";
import { formatarData } from "../../nucleo/implantacoes";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";
import {
  PERMISSAO_DE_REPASSAR,
  PERMISSAO_DE_VER_O_REPASSE,
  efeitosDoRepasse,
  tamanhoLegivel,
  useReenviarEmailsDoRepasse,
  useReprocessarRepasse,
  useRepassarModulo,
  useRepasseDaImplantacao,
  type ModuloDoRepasse,
  type RecursoDoRepasse,
  type ResumoDoRepasse,
} from "../../nucleo/repasse";

const CHIP_DO_RECURSO = { concluido: "ok", cancelado: "neutro", em_aberto: "atencao" } as const;

export function RepasseDosModulos({ implantacaoId }: { implantacaoId: string }) {
  const sessao = useSessao();
  const pode = podeNaSessao(sessao.data, PERMISSAO_DE_VER_O_REPASSE);
  const podeRepassar = podeNaSessao(sessao.data, PERMISSAO_DE_REPASSAR);
  const repasse = useRepasseDaImplantacao(implantacaoId, pode);

  if (!pode) return null;

  return (
    <section className="mb-6" aria-labelledby="titulo-do-repasse" data-testid="repasse-dos-modulos">
      <h2 className="titulo-secao" id="titulo-do-repasse">
        Módulos e repasse ao atendimento
      </h2>
      {repasse.isPending ? (
        <p className="lead" role="status">
          Carregando os módulos…
        </p>
      ) : repasse.isError ? (
        <Aviso tom="erro" data-testid="falha-do-repasse">
          {mensagemDoConsole(repasse.error)}
        </Aviso>
      ) : (
        <>
          <p className="lead">
            Cada módulo é repassado quando a tarefa dele chega ao fim do Acompanhamento. O repasse
            conclui a tarefa no Jira, gera o termo de encerramento em PDF, avisa o cliente por
            e-mail e, no primeiro módulo, libera o menu Chamados dele.
          </p>
          {repasse.data.epico.concluido ? (
            <Aviso tom="sucesso" className="mb-4" data-testid="implantacao-concluida">
              <b>Implantação concluída.</b> Todos os módulos foram repassados
              {repasse.data.epico.chave ? ` e o épico ${repasse.data.epico.chave} vai para Concluído` : ""}
              ; a conta segue com o atendimento.
              {repasse.data.epico.pendencia ? (
                <>
                  {" "}
                  <b>O Jira ainda não fechou o épico:</b> {repasse.data.epico.pendencia}
                </>
              ) : null}
            </Aviso>
          ) : null}
          {repasse.data.aviso_geotask ? (
            <Aviso tom="atencao" className="mb-4" data-testid="aviso-do-geotask">
              {repasse.data.aviso_geotask}
            </Aviso>
          ) : null}
          {!repasse.data.kickoff_fechado ? (
            <Aviso tom="atencao" className="mb-4" data-testid="repasse-sem-kickoff">
              O kickoff ainda não foi aceito: nenhum módulo pode ser repassado antes disso.
            </Aviso>
          ) : null}
          {repasse.data.modulos.length === 0 ? (
            <Aviso tom="marca">Esta implantação não tem módulo contratado.</Aviso>
          ) : (
            <div className="grid gap-3" data-testid="modulos-do-repasse">
              {repasse.data.modulos.map((modulo) => (
                <CartaoDoModulo
                  key={modulo.chave}
                  implantacaoId={implantacaoId}
                  modulo={modulo}
                  podeRepassar={podeRepassar}
                />
              ))}
            </div>
          )}
          {repasse.data.recursos_sem_modulo.length > 0 ? (
            <Cartao
              titulo="Recursos Plus sem módulo"
              className="mt-3"
              data-testid="recursos-sem-modulo"
            >
              <p className="cartao__texto mb-2">
                Infraestrutura, Interno e Migração de ERP não pertencem a um módulo. Eles travam
                só o repasse do <b>último</b> módulo: o épico só fecha com todos fechados.
              </p>
              <ListaDeRecursos recursos={repasse.data.recursos_sem_modulo} />
            </Cartao>
          ) : null}
        </>
      )}
    </section>
  );
}

function ListaDeRecursos({ recursos }: { recursos: RecursoDoRepasse[] }) {
  return (
    <ul className="lista-de-marcas">
      {recursos.map((recurso) => (
        <li className="lista-de-marcas__item" key={recurso.chave}>
          {recurso.nome}{" "}
          <Chip estado={CHIP_DO_RECURSO[recurso.situacao]} data-testid={`recurso-${recurso.chave}`}>
            {recurso.situacao_rotulo}
          </Chip>
          <span className="dado-secundario">
            {recurso.issue ? `${recurso.issue} · ` : ""}
            {recurso.status}
          </span>
        </li>
      ))}
    </ul>
  );
}

function CartaoDoModulo({
  implantacaoId,
  modulo,
  podeRepassar,
}: {
  implantacaoId: string;
  modulo: ModuloDoRepasse;
  podeRepassar: boolean;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const situacao = modulo.repassado ? (
    <Chip estado="ok">Repassado</Chip>
  ) : modulo.rejeitado ? (
    <Chip estado="neutro">Rejeitado no Jira</Chip>
  ) : (
    <Chip estado={modulo.pode_repassar ? "ok" : "aguardando"}>{modulo.etapa ?? "Sem leitura do Jira"}</Chip>
  );

  return (
    <Cartao
      titulo={modulo.nome}
      acessorio={situacao}
      data-testid={`repasse-do-${modulo.chave}`}
      data-estado={modulo.repassado ? "repassado" : modulo.pode_repassar ? "pronto" : "bloqueado"}
    >
      <p className="nota-rodape mb-3">
        {modulo.tarefa ? <span className="numero-tabular">{modulo.tarefa}</span> : "Tarefa ainda não lida no Jira"}
        {modulo.responsavel ? ` · ${modulo.responsavel}` : ""}
        {modulo.observado_em ? ` · visto em ${formatarInstante(modulo.observado_em) ?? "—"}` : ""}
      </p>

      {modulo.repassado ? (
        <Repassado modulo={modulo} resumo={modulo.repassado} podeRepassar={podeRepassar} />
      ) : modulo.rejeitado ? (
        <Aviso tom="neutro">
          A tarefa deste módulo foi rejeitada no Jira: não há o que repassar, e ele não impede o
          fechamento do épico.
        </Aviso>
      ) : (
        <>
          <ul className="lista-de-marcas mb-3" data-testid={`condicoes-de-${modulo.chave}`}>
            {modulo.condicoes.map((condicao) => (
              <li className="lista-de-marcas__item" key={condicao.chave}>
                <Chip
                  estado={condicao.cumprida ? "ok" : "atencao"}
                  data-testid={`condicao-${modulo.chave}-${condicao.chave}`}
                >
                  {condicao.cumprida ? "ok" : "falta"}
                </Chip>{" "}
                {condicao.rotulo}
                <span className="dado-secundario">{condicao.detalhe}</span>
              </li>
            ))}
          </ul>
          {modulo.recursos.length > 0 ? (
            <div className="mb-3" data-testid={`recursos-do-${modulo.chave}`}>
              <b className="campo__rotulo">Recursos Plus deste módulo</b>
              <ListaDeRecursos recursos={modulo.recursos} />
            </div>
          ) : null}
          {confirmando ? (
            <ConfirmacaoDoRepasse
              implantacaoId={implantacaoId}
              modulo={modulo}
              aoFechar={() => setConfirmando(false)}
            />
          ) : (
            <div className="acoes-do-console">
              <Botao
                data-testid={`repassar-${modulo.chave}`}
                disabled={!modulo.pode_repassar || !podeRepassar}
                aria-describedby={`motivo-${modulo.chave}`}
                onClick={() => setConfirmando(true)}
              >
                Concluir e repassar ao atendimento
              </Botao>
              <p
                className="nota-rodape"
                id={`motivo-${modulo.chave}`}
                data-testid={`motivo-do-bloqueio-${modulo.chave}`}
              >
                {!podeRepassar
                  ? "O seu perfil não repassa módulos: isso é da consultoria."
                  : modulo.pode_repassar
                    ? "Tudo pronto: o módulo pode ser repassado."
                    : modulo.motivos.join(". ") + "."}
              </p>
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}

function ConfirmacaoDoRepasse({
  implantacaoId,
  modulo,
  aoFechar,
}: {
  implantacaoId: string;
  modulo: ModuloDoRepasse;
  aoFechar: () => void;
}) {
  const repassar = useRepassarModulo(implantacaoId, modulo.chave);
  const [observacao, setObservacao] = useState("");
  const [treinamentos, setTreinamentos] = useState<string[]>(
    modulo.treinamentos.filter((t) => t.sugerido).map((t) => t.reuniao_id),
  );
  const [chamados, setChamados] = useState<string[]>(
    modulo.chamados.filter((c) => c.sugerido).map((c) => c.ticket_id),
  );
  const alternar = (lista: string[], definir: (valor: string[]) => void, id: string, marcado: boolean) =>
    definir(marcado ? [...lista, id] : lista.filter((item) => item !== id));

  return (
    <div className="cartao--grave" data-testid={`confirmacao-do-repasse-${modulo.chave}`}>
      <p className="cartao__texto" role="status">
        <b>Concluir e repassar o {modulo.nome} ao atendimento?</b> O ato é registrado com o seu nome
        e não se desfaz.
      </p>

      <fieldset className="mb-3" data-testid="treinamentos-do-repasse">
        <legend className="campo__rotulo">Treinamentos realizados deste módulo</legend>
        {modulo.treinamentos.length === 0 ? (
          <p className="nota-rodape">
            Nenhum treinamento realizado e visível ao cliente que já não conste do termo de outro
            módulo.
          </p>
        ) : (
          modulo.treinamentos.map((treinamento) => (
            <label key={treinamento.reuniao_id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={treinamentos.includes(treinamento.reuniao_id)}
                onChange={(e) =>
                  alternar(treinamentos, setTreinamentos, treinamento.reuniao_id, e.target.checked)
                }
              />
              <span>
                {treinamento.assunto} · {formatarData(treinamento.data) ?? treinamento.data} ·{" "}
                {treinamento.com_ata ? "com ata" : "sem ata"} ·{" "}
                {treinamento.com_gravacao ? "com gravação" : "sem gravação"}
              </span>
            </label>
          ))
        )}
      </fieldset>

      <fieldset className="mb-3" data-testid="chamados-do-repasse">
        <legend className="campo__rotulo">Pendências que seguem no atendimento (chamados em aberto)</legend>
        {modulo.chamados.length === 0 ? (
          <p className="nota-rodape">
            Nenhum chamado em aberto na conta. Para uma pendência seguir com número, abra o
            chamado antes de repassar.
          </p>
        ) : (
          modulo.chamados.map((chamado) => (
            <label key={chamado.ticket_id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={chamados.includes(chamado.ticket_id)}
                onChange={(e) => alternar(chamados, setChamados, chamado.ticket_id, e.target.checked)}
              />
              <span>
                #{chamado.numero} — {chamado.assunto}
                {chamado.chave_no_atd ? ` (${chamado.chave_no_atd})` : ""}
              </span>
            </label>
          ))
        )}
      </fieldset>

      <CampoLongo
        rotulo="Observação para o termo (opcional)"
        value={observacao}
        maxLength={2000}
        onChange={(e) => setObservacao(e.target.value)}
      />

      <div className="mb-3" data-testid="efeitos-do-repasse">
        <b className="campo__rotulo">O que este repasse dispara</b>
        <ul className="lista-de-marcas">
          {efeitosDoRepasse(modulo).map((efeito) => (
            <li className="lista-de-marcas__item" key={efeito}>
              {efeito}
            </li>
          ))}
        </ul>
      </div>

      {repassar.isError ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-do-repasse">
          {mensagemDoConsole(repassar.error)}
          {repassar.error instanceof ErroDaApi && repassar.error.status === 409
            ? " A tela vai se atualizar com o estado de agora."
            : null}
        </Aviso>
      ) : null}
      <div className="acoes-do-console">
        <Botao
          data-testid={`confirmar-repasse-${modulo.chave}`}
          disabled={repassar.isPending}
          onClick={() =>
            repassar.mutate(
              { observacao: observacao.trim() || null, treinamentos, chamados },
              { onSuccess: aoFechar },
            )
          }
        >
          {repassar.isPending ? "Repassando…" : "Confirmar o repasse"}
        </Botao>
        <Botao variante="fantasma" disabled={repassar.isPending} onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}

function Repassado({
  modulo,
  resumo,
  podeRepassar,
}: {
  modulo: ModuloDoRepasse;
  resumo: ResumoDoRepasse;
  podeRepassar: boolean;
}) {
  const reprocessar = useReprocessarRepasse();
  const reenviar = useReenviarEmailsDoRepasse();
  const { emails } = resumo;

  return (
    <div data-testid={`repassado-${modulo.chave}`}>
      <p className="cartao__texto">
        Repassado em {formatarInstante(resumo.repassado_em) ?? "—"} por {resumo.repassado_por}.
        {resumo.liberou_chamados ? " Este repasse abriu o menu Chamados do cliente." : ""}
        {resumo.ultimo ? " Foi o último módulo: a implantação está concluída." : ""}
      </p>
      {resumo.observacao ? (
        <p className="nota-rodape mt-1">Observação do termo: {resumo.observacao}</p>
      ) : null}
      <p className="mt-2">
        <a
          className="botao botao--fantasma botao--pequeno"
          href={caminhoDoTermoDeEncerramento(resumo.id)}
          data-testid={`termo-${modulo.chave}`}
        >
          Baixar o termo (PDF, {tamanhoLegivel(resumo.termo.tamanho)})
        </a>
      </p>

      {resumo.jira.pendente ? (
        <Aviso tom="atencao" className="mt-3" data-testid={`jira-pendente-${modulo.chave}`}>
          <b>O Jira ainda tem ação pendente neste repasse.</b> O portal repete sozinho; o repasse
          em si já está valendo.
          <ul className="lista-de-marcas mt-2">
            {resumo.jira.termo_anexado_em === null ? (
              <li className="lista-de-marcas__item">
                Termo na tarefa
                <span className="dado-secundario">{resumo.jira.termo_anexo_erro ?? "Aguardando a fila."}</span>
              </li>
            ) : null}
            {resumo.jira.tarefa_fechada_em === null ? (
              <li className="lista-de-marcas__item">
                Concluir a tarefa
                <span className="dado-secundario">{resumo.jira.tarefa_erro ?? "Aguardando a fila."}</span>
              </li>
            ) : null}
          </ul>
          {podeRepassar ? (
            <Botao
              tamanho="pequeno"
              variante="fantasma"
              className="mt-2"
              disabled={reprocessar.isPending}
              data-testid={`reprocessar-${modulo.chave}`}
              onClick={() => reprocessar.mutate(resumo.id)}
            >
              {reprocessar.isPending ? "Reprocessando…" : "Reprocessar no Jira"}
            </Botao>
          ) : null}
          {reprocessar.isError ? (
            <p className="campo__erro" role="alert">
              {mensagemDoConsole(reprocessar.error)}
            </p>
          ) : null}
        </Aviso>
      ) : (
        <p className="nota-rodape mt-2" data-testid={`jira-ok-${modulo.chave}`}>
          <Chip estado="ok">Jira</Chip> Termo anexado e tarefa {modulo.tarefa ?? ""} concluída.
        </p>
      )}

      <p className="nota-rodape mt-2" data-testid={`emails-do-${modulo.chave}`}>
        <Chip estado={emails.falhas > 0 ? "atencao" : emails.pendentes > 0 ? "aguardando" : "ok"}>
          E-mail
        </Chip>{" "}
        {emails.enviados} de {emails.destinatarios}{" "}
        {emails.destinatarios === 1 ? "pessoa recebeu" : "pessoas receberam"} a formalização
        {emails.pendentes > 0 ? ` · ${emails.pendentes} na fila` : ""}
        {emails.falhas > 0 ? ` · ${emails.falhas} sem entrega` : ""}.
        {emails.ultimo_erro ? ` Último erro: ${emails.ultimo_erro}` : ""}
      </p>
      {emails.falhas > 0 && podeRepassar ? (
        <Botao
          tamanho="pequeno"
          variante="fantasma"
          className="mt-1"
          disabled={reenviar.isPending}
          data-testid={`reenviar-${modulo.chave}`}
          onClick={() => reenviar.mutate(resumo.id)}
        >
          {reenviar.isPending ? "Reenviando…" : "Reenviar a quem não recebeu"}
        </Botao>
      ) : null}
      {reenviar.isError ? (
        <p className="campo__erro" role="alert">
          {mensagemDoConsole(reenviar.error)}
        </p>
      ) : null}
    </div>
  );
}
